import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCheck, Eye, Flag, ShieldAlert, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmReasonDialog } from "@/components/common/ConfirmReasonDialog";
import { EmptyState, ErrorState, LoadingState, Notice, StatusBadge } from "@/components/common/States";
import { api, errorMessage, fetcher } from "@/lib/api";
import { REPORT_STATE, timeAgo } from "@/lib/labels";

export function ModerationPanel({ onRefresh }) {
  const [status, setStatus] = useState("open");
  const [tindak, setTindak] = useState(null);
  const q = useQuery({ queryKey: ["feed-reports", status], queryFn: () => fetcher(`/school/feed/reports?status=${status}`) });
  const ringkas = useQuery({ queryKey: ["feed-moderation"], queryFn: () => fetcher("/school/feed/moderation") });

  const segarkan = () => {
    q.refetch();
    ringkas.refetch();
    onRefresh?.();
  };
  const putus = useMutation({
    mutationFn: async ({ id, action, hide, note }) => (await api.post(`/school/feed/reports/${id}/resolve`,
      { action, hide_target: !!hide, note: note || null })).data,
    onSuccess: (d) => {
      toast.success(d.status === "resolved"
        ? (d.target ? "Laporan ditindaklanjuti dan konten disembunyikan." : "Laporan ditandai selesai.")
        : "Laporan ditolak.");
      segarkan();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const m = ringkas.data;
  return (
    <div className="space-y-4" data-testid="moderation-panel">
      {m && (
        <div className="grid grid-cols-3 gap-3" data-testid="moderation-counts">
          {[["Laporan menunggu", m.open_reports], ["Postingan ditarik", m.hidden_posts], ["Komentar disembunyikan", m.hidden_comments]].map(([label, n]) => (
            <div key={label} className="rounded-xl border bg-card p-3">
              <p className="font-brand text-2xl font-extrabold tabular-nums">{n}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
      )}
      <Notice tone="muted" testId="moderation-hint">
        Komentar yang disembunyikan tetap dapat dipulihkan dari dalam utas postingannya.
      </Notice>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Saring laporan">
        {[["open", "Menunggu"], ["resolved", "Ditindaklanjuti"], ["dismissed", "Ditolak"], ["all", "Semua"]].map(([v, label]) => (
          <Button key={v} size="sm" variant={status === v ? "default" : "outline"} onClick={() => setStatus(v)} data-testid={`report-filter-${v}`}>
            {label}
          </Button>
        ))}
      </div>
      {q.isLoading ? <LoadingState rows={3} /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} />
        : !q.data.reports.length ? (
          <EmptyState title={status === "open" ? "Tidak ada laporan yang menunggu" : "Belum ada laporan pada saringan ini"}
            description="Laporan dari siswa dan orang tua akan muncul di sini begitu dikirim." />
        ) : (
          <ul className="space-y-3" data-testid="report-list">
            {q.data.reports.map((r) => (
              <li key={r.id} className="rounded-xl border bg-card p-4" data-testid="report-item">
                <div className="flex flex-wrap items-center gap-2">
                  <Flag className="h-4 w-4 text-[#E11D2E]" aria-hidden />
                  <p className="text-sm font-semibold">{r.reason_label}</p>
                  <StatusBadge map={REPORT_STATE} value={r.status} testId="report-state" />
                  <span className="ml-auto text-xs text-muted-foreground">{timeAgo(r.created_at)}</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground" data-testid="report-meta">
                  {r.target_label} · dilaporkan oleh {r.reporter?.full_name || "anggota"}
                </p>
                {r.note && <p className="mt-1 rounded-lg bg-muted px-3 py-2 text-sm italic" data-testid="report-note">“{r.note}”</p>}
                {r.target_snippet && (
                  <p className="mt-2 line-clamp-3 rounded-lg border border-dashed px-3 py-2 text-sm" data-testid="report-snippet">{r.target_snippet}</p>
                )}
                {r.resolution_note && <p className="mt-2 text-xs text-muted-foreground">Catatan keputusan: {r.resolution_note}</p>}
                {r.status === "open" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="destructive" disabled={putus.isPending} data-testid="report-hide-resolve"
                      onClick={() => setTindak(r)}>
                      <ShieldAlert className="mr-1.5 h-4 w-4" /> Sembunyikan &amp; selesaikan
                    </Button>
                    <Button size="sm" variant="outline" disabled={putus.isPending} data-testid="report-resolve"
                      onClick={() => putus.mutate({ id: r.id, action: "resolve", hide: false })}>
                      <CheckCheck className="mr-1.5 h-4 w-4" /> Konten sah, selesaikan
                    </Button>
                    <Button size="sm" variant="ghost" disabled={putus.isPending} data-testid="report-dismiss"
                      onClick={() => putus.mutate({ id: r.id, action: "dismiss", hide: false })}>
                      <X className="mr-1.5 h-4 w-4" /> Tolak laporan
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      <ConfirmReasonDialog open={!!tindak} onOpenChange={(o) => !o && setTindak(null)} danger testId="report-hide-dialog"
        title="Sembunyikan konten dan selesaikan laporan" confirmLabel="Sembunyikan konten"
        description="Konten tidak lagi terlihat warga sekolah dan penulisnya diberi tahu. Alasan Anda tercatat di log audit."
        onConfirm={async (note) => {
          await api.post(`/school/feed/reports/${tindak.id}/resolve`, { action: "resolve", hide_target: true, note });
          toast.success("Konten disembunyikan dan laporan diselesaikan.");
          setTindak(null);
          segarkan();
        }} />
      <Button variant="ghost" size="sm" className="gap-1.5" onClick={segarkan} data-testid="moderation-refresh">
        <Eye className="h-4 w-4" /> Muat ulang
      </Button>
    </div>
  );
}
